import { lazy, Suspense } from "react";
import { createBrowserRouter, Navigate } from "react-router";
import { RequireAuth } from "@/components/auth/RequireAuth";
import { RequireAdmin } from "@/components/auth/RequireAdmin";
import { RedirectIfAuthenticated } from "@/components/auth/RedirectIfAuthenticated";
import { AppLayout } from "@/components/layout/AppLayout";
import { AuthLayout } from "@/components/layout/AuthLayout";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { Skeleton } from "@/components/ui/skeleton";

// Lazy-loaded pages
const LoginPage = lazy(() => import("@/pages/LoginPage"));
const VerifyPage = lazy(() => import("@/pages/VerifyPage"));
const ExpiredPage = lazy(() => import("@/pages/ExpiredPage"));
const StocksPage = lazy(() => import("@/pages/StocksPage"));
const StockDetailPage = lazy(() => import("@/pages/StockDetailPage"));
const AttentionPage = lazy(() => import("@/pages/AttentionPage"));
const SettingsPage = lazy(() => import("@/pages/SettingsPage"));
const OnboardingPage = lazy(() => import("@/pages/OnboardingPage"));
const AdminDashboardPage = lazy(() => import("@/pages/AdminDashboardPage"));
const AdminUploadPage = lazy(() => import("@/pages/AdminUploadPage"));
const AdminSubscribersPage = lazy(() => import("@/pages/AdminSubscribersPage"));

function PageSuspense({ children }: { children: React.ReactNode }) {
  return (
    <Suspense
      fallback={
        <div className="flex min-h-[400px] items-center justify-center">
          <Skeleton className="h-10 w-10 rounded-full" />
        </div>
      }
    >
      {children}
    </Suspense>
  );
}

export const router = createBrowserRouter([
  // Root redirect
  {
    path: "/",
    element: <Navigate to="/stocks" replace />,
  },

  // Auth routes (redirect if already authenticated)
  {
    element: <RedirectIfAuthenticated />,
    children: [
      {
        element: <AuthLayout />,
        children: [
          {
            path: "/login",
            element: (
              <PageSuspense>
                <LoginPage />
              </PageSuspense>
            ),
          },
          {
            path: "/verify",
            element: (
              <PageSuspense>
                <VerifyPage />
              </PageSuspense>
            ),
          },
        ],
      },
    ],
  },

  // Expired route (AuthLayout only, no redirect guard)
  {
    element: <AuthLayout />,
    children: [
      {
        path: "/expired",
        element: (
          <PageSuspense>
            <ExpiredPage />
          </PageSuspense>
        ),
      },
    ],
  },

  // Protected routes
  {
    element: <RequireAuth />,
    children: [
      {
        element: <AppLayout />,
        children: [
          {
            path: "/stocks",
            element: (
              <PageSuspense>
                <StocksPage />
              </PageSuspense>
            ),
          },
          {
            path: "/stocks/:ticker",
            element: (
              <PageSuspense>
                <StockDetailPage />
              </PageSuspense>
            ),
          },
          {
            path: "/attention",
            element: (
              <PageSuspense>
                <AttentionPage />
              </PageSuspense>
            ),
          },
          {
            path: "/settings",
            element: (
              <PageSuspense>
                <SettingsPage />
              </PageSuspense>
            ),
          },
          {
            path: "/onboarding",
            element: (
              <PageSuspense>
                <OnboardingPage />
              </PageSuspense>
            ),
          },
        ],
      },

      // Admin routes (nested under RequireAuth)
      {
        element: <RequireAdmin />,
        children: [
          {
            element: <AdminLayout />,
            children: [
              {
                path: "/admin",
                element: (
                  <PageSuspense>
                    <AdminDashboardPage />
                  </PageSuspense>
                ),
              },
              {
                path: "/admin/upload",
                element: (
                  <PageSuspense>
                    <AdminUploadPage />
                  </PageSuspense>
                ),
              },
              {
                path: "/admin/subscribers",
                element: (
                  <PageSuspense>
                    <AdminSubscribersPage />
                  </PageSuspense>
                ),
              },
            ],
          },
        ],
      },
    ],
  },

  // Catch-all
  {
    path: "*",
    element: <Navigate to="/stocks" replace />,
  },
]);
